<script>
  import { onMount } from "svelte";
  import { OverflowMenu, OverflowMenuItem } from "carbon-components-svelte";
  import { useLocation } from "svelte-navigator";
  import {
    Accordion,
    Tooltip,
    SectionWrapper,
    Button,
  } from "paxray-components-svelte";
  import ContentSwitcher from "lib/components/ContentSwitcher.svelte";
  import Header from "lib/components/Header.svelte";
  import PaxbotChat from "lib/components/paxbot/PaxbotChat.svelte";
  import LineChartNeu from "lib/components/LineChartNeu.svelte";
  import { uiFeatureStore } from "@stores/uiFeatureStore.js";
  import { fetchWithSession } from "../../../../lib/util/fetchWithSession";
  import { createRequestScope } from "lib/util/requestScope.js";
  import { fetchWrapper } from "lib/util/fetchWrapper.js";
  import { ROUTES } from "@utils/routes.js";
  import BpmnPreview from "./components/changeAnalysis/BpmnPreview.svelte";
  import ChangeListTable from "./components/changeAnalysis/ChangeListTable.svelte";
  import ModelQuantificationCard from "./components/changeAnalysis/ModelQuantificationCard.svelte";
  import ProcessModelSelectionModal from "./components/changeAnalysis/ProcessModelSelectionModal.svelte";
  import { diffBpmnModels } from "./components/changeAnalysis/bpmnDiff.js";
  import Filter from "./components/filter/Filter.svelte";
  import {
    BOT_ICON,
    CHEVRON_DOWN_ICON,
    CHEVRON_UP_ICON,
    DOWNLOAD_ICON,
    FULLSCREEN_ICON,
    FULLSCREEN_OFF_ICON,
    TIMELINE_ICON,
  } from "@utils/icons.js";

  // Requests der Maske enden, wenn die Maske verlassen wird.
  const abortSignal = createRequestScope();
  export let idproject;

  const location = useLocation();
  $: isProcessIntelligenceChangeAnalysis =
    $location?.pathname === ROUTES.monitoring.processChangeAnalysis;

  let beforeModel = null;
  let afterModel = null;
  let modelPickerOpen = false;
  let changeSummary = [];
  let beforeMarkers = [];
  let afterMarkers = [];
  let changesOpen = false;
  let diffError = "";
  let diffLoading = false;
  let lastDiffKey = "";
  let diffRequestId = 0;
  let hoveredChangeId = "";
  let selectedChangeId = "";
  let changeFilterMode = "all";
  let selectedChangeFilters = [];
  let demoModelsLoaded = false;
  let paxbotOpen = false;
  let fullscreenPane = "";
  let comparisonCandidates = [];
  let comparisonLoading = false;
  let comparisonError = "";
  let lastComparisonBaselineKey = "";
  let comparisonRequestId = 0;
  let bpmnPreview;
  let comparisonPanelOpen = true;
  let analysisView = "model";
  let modelViewSelection = 0;
  let lineChart;
  let timelineModels = [];
  let selectedTimelineModelIds = [];
  let timelineLoading = false;
  let timelineError = "";
  let timelineRequestId = 0;
  let timelineSearch = "";
  let savedProcessModelsLoading = true;
  let savedProcessModelsAvailable = true;
  let savedProcessModelsError = "";
  const changeFilters = ["Added", "Removed", "Changed", "Layout Changed"];
  const demoModels = [
    {
      id: "demo-task-a",
      side: "before",
      name: "Demo Baseline",
      path: "/demo/change-analysis/Demo Task A.bpmn",
      daysAgo: 42,
      area: "Field Operations",
      activitiesCount: 4,
      appsCount: 2,
      activityMetrics: [
        { weighted_time_seconds: 75 },
        { weighted_time_seconds: 180 },
        { weighted_time_seconds: 90 },
        { weighted_time_seconds: 60 },
      ],
    },
    {
      id: "demo-task-b",
      side: "after",
      name: "Demo Comparison",
      path: "/demo/change-analysis/Demo Task B.bpmn",
      daysAgo: 7,
      area: "Field Operations",
      activitiesCount: 3,
      appsCount: 2,
      activityMetrics: [
        { weighted_time_seconds: 70 },
        { weighted_time_seconds: 160 },
        { weighted_time_seconds: 55 },
      ],
    },
  ];
  const markerFilterClasses = {
    Added: "change_added",
    Removed: "change_removed",
    Changed: "change_changed",
    "Layout Changed": "change_layout_changed",
  };
  const analysisViewOptions = {
    model: {
      text: "Change Analysis",
      icon: "changes",
    },
    comparison: {
      text: "Model Comparison",
      icon: "split_scene",
    },
    timeline: {
      text: "Change History",
      icon: "show_chart",
    },
  };
  const analysisViewOrder = ["model", "comparison", "timeline"];

  $: hasBothModels = beforeModel && afterModel;
  $: diffKey = hasBothModels
    ? `${beforeModel.bpmnXml}::${afterModel.bpmnXml}`
    : "";
  $: activeChangeId = selectedChangeId || hoveredChangeId;
  $: filteredChangeSummary =
    changeFilterMode === "none"
      ? []
      : selectedChangeFilters.length
        ? changeSummary.filter((item) =>
            selectedChangeFilters.includes(item.change),
          )
        : changeSummary;
  $: activeMarkerClasses = selectedChangeFilters.map(
    (filter) => markerFilterClasses[filter],
  );
  $: filteredBeforeMarkers =
    changeFilterMode === "none"
      ? []
      : selectedChangeFilters.length
        ? beforeMarkers.filter((marker) =>
            activeMarkerClasses.includes(marker.className),
          )
        : beforeMarkers;
  $: filteredAfterMarkers =
    changeFilterMode === "none"
      ? []
      : selectedChangeFilters.length
        ? afterMarkers.filter((marker) =>
            activeMarkerClasses.includes(marker.className),
          )
        : afterMarkers;
  $: displayedModel =
    modelViewSelection === 1 && afterModel ? afterModel : beforeModel;
  $: displayedMarkers =
    modelViewSelection === 1 && afterModel
      ? filteredAfterMarkers
      : filteredBeforeMarkers;
  $: visibleComparisonCandidates = comparisonCandidates;
  $: selectedTimelineModels = timelineModels.filter((model) =>
    selectedTimelineModelIds.includes(getModelKey(model)),
  );
  $: latestTimelineModel = getLatestTimelineModel(selectedTimelineModels);
  $: visibleTimelineModels = timelineModels.filter((model) =>
    normalizeModelName(model.name).includes(normalizeModelName(timelineSearch)),
  );
  $: timelineDataSets = getTimelineDataSets(selectedTimelineModels);
  $: changeCounts = changeFilters.reduce((counts, filter) => {
    counts[filter] = changeSummary.filter(
      (item) => item.change === filter,
    ).length;
    return counts;
  }, {});
  $: activeAnalysisViewOption =
    analysisViewOptions[analysisView] || analysisViewOptions.model;
  $: changeAnalysisEmptyState =
    !$uiFeatureStore.analysisViews.changeAnalysisDemoModels &&
    !savedProcessModelsLoading &&
    !savedProcessModelsError &&
    !savedProcessModelsAvailable;
  $: if (
    $uiFeatureStore.analysisViews.changeAnalysisDemoModels &&
    !demoModelsLoaded &&
    !beforeModel &&
    !afterModel
  )
    loadDemoModels();
  $: if (hasBothModels && diffKey !== lastDiffKey)
    updateChangeSummary(beforeModel.bpmnXml, afterModel.bpmnXml, diffKey);
  $: comparisonBaselineKey = beforeModel
    ? `${beforeModel.id || ""}:${getBaseModelName(beforeModel.name)}`
    : "";
  $: if (
    beforeModel &&
    !$uiFeatureStore.analysisViews.changeAnalysisDemoModels &&
    (comparisonPanelOpen || analysisView === "timeline") &&
    comparisonBaselineKey !== lastComparisonBaselineKey
  )
    loadComparisonCandidates(comparisonBaselineKey);
  $: if (!hasBothModels) {
    changeSummary = [];
    beforeMarkers = [];
    afterMarkers = [];
    hoveredChangeId = "";
    selectedChangeId = "";
    changeFilterMode = "all";
    selectedChangeFilters = [];
    diffError = "";
    diffLoading = false;
    lastDiffKey = "";
  }

  function formatDate(date) {
    return `${String(date.getDate()).padStart(2, "0")}.${String(date.getMonth() + 1).padStart(2, "0")}.${date.getFullYear()}`;
  }

  function getRelativeDemoDate(daysAgo) {
    const date = new Date();
    date.setDate(date.getDate() - daysAgo);
    return formatDate(date);
  }

  function getDemoModel(model, bpmnXml) {
    return {
      ...model,
      date: getRelativeDemoDate(model.daysAgo),
      bpmnXml,
    };
  }

  function openModelPicker() {
    modelPickerOpen = true;
  }

  async function loadSavedProcessModelAvailability() {
    savedProcessModelsLoading = true;
    savedProcessModelsError = "";

    try {
      const response = await fetchWithSession("/api/pman/processFolders", {
        signal: abortSignal,
      });
      if (!response.ok) throw new Error("Could not load saved process models.");
      const tree = await response.json();
      savedProcessModelsAvailable = collectProcessModels(tree).length > 0;
    } catch (err) {
      savedProcessModelsError =
        err.message || "Could not load saved process models.";
    } finally {
      savedProcessModelsLoading = false;
    }
  }

  function selectStoredModel(event) {
    beforeModel = { ...event.detail };
    afterModel = null;
    changesOpen = false;
    modelViewSelection = 0;
    comparisonPanelOpen = true;
    comparisonCandidates = [];
    comparisonError = "";
    comparisonLoading = false;
    lastComparisonBaselineKey = "";
    timelineModels = [];
    selectedTimelineModelIds = [];
    timelineSearch = "";
  }

  function normalizeModelName(name = "") {
    return String(name).trim().toLowerCase();
  }

  function getBaseModelName(name = "") {
    return String(name)
      .replace(/\.bpmn$/i, "")
      .replace(/(?:_\d+|\s+\(\d+\))$/, "")
      .trim();
  }

  function collectProcessModels(nodes, models = [], folderName = "") {
    (nodes || []).forEach((node) => {
      if (node.type === "process") models.push({ ...node, area: folderName });
      if (node.children?.length) {
        collectProcessModels(
          node.children,
          models,
          node.type === "folder"
            ? node.name || node.text || folderName
            : folderName,
        );
      }
    });
    return models;
  }

  async function loadComparisonCandidates(key) {
    const currentRequest = ++comparisonRequestId;
    const baselineId = beforeModel?.id;
    const baselineName = normalizeModelName(
      getBaseModelName(beforeModel?.name),
    );
    lastComparisonBaselineKey = key;
    comparisonCandidates = [];
    comparisonError = "";
    comparisonLoading = true;

    try {
      const response = await fetchWithSession("/api/pman/processFolders", {
        signal: abortSignal,
      });
      if (!response.ok) throw new Error("Could not load comparison models.");
      const tree = await response.json();
      if (currentRequest !== comparisonRequestId) return;
      comparisonCandidates = collectProcessModels(tree)
        .filter(
          (model) =>
            model.id != baselineId &&
            normalizeModelName(getBaseModelName(model.name || model.text)) ===
              baselineName,
        )
        .map((model) => ({
          ...model,
          name: model.name || model.text || "Process Model",
        }))
        .sort(
          (left, right) =>
            new Date(right.created_at || 0) - new Date(left.created_at || 0),
        );
      if (analysisView === "timeline") loadTimelineModels();
    } catch (err) {
      if (currentRequest !== comparisonRequestId) return;
      comparisonError = err.message || "Could not load comparison models.";
    } finally {
      if (currentRequest === comparisonRequestId) comparisonLoading = false;
    }
  }

  async function selectComparisonModel(candidate) {
    comparisonError = "";
    try {
      const response = await fetchWithSession(
        `/api/pman/processModel/${encodeURIComponent(candidate.id)}`,
        { signal: abortSignal, cache: "no-store" },
      );
      if (!response.ok)
        throw new Error("Could not load this comparison model.");
      const process = await response.json();
      afterModel = {
        id: process.id,
        name: process.name || candidate.name,
        date: formatDateValue(process.created_at),
        bpmnXml: process.bpmnstring,
        avgTimeSeconds:
          process.avg_time_seconds == null
            ? null
            : Number(process.avg_time_seconds),
        activityMetrics: Array.isArray(process.activity_metrics)
          ? process.activity_metrics
          : [],
        area: candidate.area,
      };
      changesOpen = true;
      modelViewSelection = 0;
    } catch (err) {
      comparisonError = err.message || "Could not load this comparison model.";
    }
  }

  function formatDateValue(value) {
    if (!value) return "";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "" : formatDate(date);
  }

  function formatDuration(value) {
    const seconds = Number(value);
    if (!Number.isFinite(seconds)) return "–";
    const rounded = Math.round(seconds);
    const minutes = Math.floor(rounded / 60);
    const remainder = rounded % 60;
    if (minutes && remainder) return `${minutes}m ${remainder}s`;
    if (minutes) return `${minutes}m`;
    return `${remainder}s`;
  }

  function getModelKey(model) {
    return String(
      model?.id ||
        `${model?.name || "model"}:${model?.date || model?.created_at || ""}`,
    );
  }

  function parseStoredDate(model) {
    if (model?.created_at) {
      const date = new Date(model.created_at);
      if (!Number.isNaN(date.getTime())) return date;
    }
    const match = String(model?.date || "").match(
      /^(\d{2})\.(\d{2})\.(\d{4})$/,
    );
    if (!match) return new Date(0);
    return new Date(Number(match[3]), Number(match[2]) - 1, Number(match[1]));
  }

  function getModelVersionNumber(model) {
    const match = String(model?.name || "").match(
      /(?:_(\d+)|\((\d+)\))(?=\.bpmn$|$)/i,
    );
    return Number(match?.[1] || match?.[2] || 0);
  }

  function getLatestTimelineModel(models) {
    return models.reduce((latest, model) => {
      if (!latest) return model;
      const dateDifference = parseStoredDate(model) - parseStoredDate(latest);
      if (dateDifference !== 0) return dateDifference > 0 ? model : latest;
      return getModelVersionNumber(model) > getModelVersionNumber(latest)
        ? model
        : latest;
    }, null);
  }

  function getTimelineStats(model) {
    const directTime = Number(model.avgTimeSeconds ?? model.avg_time_seconds);
    const activityMetrics = Array.isArray(model.activityMetrics)
      ? model.activityMetrics
      : [];
    let timeSeconds = Number.isFinite(directTime) ? directTime : null;
    if (timeSeconds === null && activityMetrics.length) {
      timeSeconds = activityMetrics.reduce((total, metric) => {
        const weightedTime = Number(metric?.weighted_time_seconds);
        if (Number.isFinite(weightedTime)) return total + weightedTime;
        const avgTime = Number(metric?.avg_time_seconds);
        const frequency = Number(metric?.frequency);
        return Number.isFinite(avgTime) && Number.isFinite(frequency)
          ? total + avgTime * frequency
          : total;
      }, 0);
    }
    let activities = Number(model.activitiesCount ?? model.activities_count);
    let apps = Number(model.appsCount ?? model.apps_count);
    if (
      (!Number.isFinite(activities) || !Number.isFinite(apps)) &&
      model?.bpmnXml &&
      typeof DOMParser !== "undefined"
    ) {
      const document = new DOMParser().parseFromString(
        model.bpmnXml,
        "application/xml",
      );
      const elements = Array.from(document.getElementsByTagName("*"));
      const activityTypes = new Set([
        "businessRuleTask",
        "callActivity",
        "manualTask",
        "receiveTask",
        "scriptTask",
        "sendTask",
        "serviceTask",
        "subProcess",
        "task",
        "userTask",
      ]);
      const parsedApps = new Set(
        elements
          .filter((element) => element.localName === "dataStoreReference")
          .map((element) => element.getAttribute("name"))
          .filter(Boolean),
      );
      if (!Number.isFinite(activities)) {
        activities = elements.filter((element) =>
          activityTypes.has(element.localName),
        ).length;
      }
      if (!Number.isFinite(apps)) apps = parsedApps.size;
    }
    return {
      time: timeSeconds === null ? null : timeSeconds / 60,
      activities: Number.isFinite(activities) ? activities : null,
      apps: Number.isFinite(apps) ? apps : null,
    };
  }

  function getTimelineDataSets(models) {
    const points = [...models]
      .map((model) => ({ model, stats: getTimelineStats(model) }))
      .filter((item) => item.stats)
      .sort(
        (left, right) =>
          parseStoredDate(left.model) - parseStoredDate(right.model),
      );
    const dataset = (label, key) => ({
      label,
      data: points
        .filter((item) => Number.isFinite(item.stats[key]))
        .map((item) => ({
          // parseStoredDate liefert ohne Datum new Date(0); das gilt nur fuer die Sortierung.
          x:
            formatDateValue(item.model.created_at) ||
            item.model.date ||
            "No date available",
          y: item.stats[key],
        })),
    });
    return [
      dataset("Avg Time (min)", "time"),
      dataset("Activities", "activities"),
      dataset("Apps", "apps"),
    ];
  }

  async function loadTimelineModel(model) {
    if (model.bpmnXml) return model;
    const response = await fetchWithSession(
      `/api/pman/processModel/${encodeURIComponent(model.id)}`,
      { signal: abortSignal, cache: "no-store" },
    );
    if (!response.ok) throw new Error(`Could not load ${model.name}.`);
    const process = await response.json();
    return {
      ...model,
      id: process.id,
      name: process.name || model.name,
      created_at: process.created_at || model.created_at,
      date: formatDateValue(process.created_at || model.created_at),
      bpmnXml: process.bpmnstring,
      avgTimeSeconds:
        process.avg_time_seconds == null
          ? null
          : Number(process.avg_time_seconds),
      activityMetrics: Array.isArray(process.activity_metrics)
        ? process.activity_metrics
        : [],
    };
  }

  async function loadTimelineModels() {
    if (!beforeModel) return;
    const requestId = ++timelineRequestId;
    timelineLoading = true;
    timelineError = "";
    try {
      const uniqueModels = [beforeModel, ...comparisonCandidates].filter(
        (model, index, models) =>
          models.findIndex(
            (candidate) => getModelKey(candidate) === getModelKey(model),
          ) === index,
      );
      const loadedModels = await Promise.all(
        uniqueModels.map(loadTimelineModel),
      );
      if (requestId !== timelineRequestId) return;
      timelineModels = loadedModels;
      const availableIds = loadedModels.map(getModelKey);
      selectedTimelineModelIds = selectedTimelineModelIds.filter((id) =>
        availableIds.includes(id),
      );
      if (!selectedTimelineModelIds.length) {
        selectedTimelineModelIds = availableIds;
      }
    } catch (err) {
      if (requestId !== timelineRequestId) return;
      timelineError = err.message || "Could not load model history.";
    } finally {
      if (requestId === timelineRequestId) timelineLoading = false;
    }
  }

  function setAnalysisView(view) {
    if (analysisView === view) return;
    analysisView = view;
    fullscreenPane = "";
    if (analysisView === "timeline") loadTimelineModels();
  }

  function toggleTimelineModel(model) {
    const id = getModelKey(model);
    selectedTimelineModelIds = selectedTimelineModelIds.includes(id)
      ? selectedTimelineModelIds.filter((selectedId) => selectedId !== id)
      : [...selectedTimelineModelIds, id];
  }

  function downloadCurrentView() {
    if (analysisView === "timeline") {
      lineChart?.download(
        getBaseModelName(beforeModel?.name) || "change-history",
      );
      return;
    }
    bpmnPreview?.downloadSVG(
      `${getBaseModelName(displayedModel?.name) || "change-analysis"}.svg`,
    );
  }

  async function loadDemoModels() {
    demoModelsLoaded = true;
    try {
      const loadedModels = await Promise.all(
        demoModels.map(async (model) => {
          const response = await fetchWrapper(model.path);
          if (!response?.ok) return null;
          return getDemoModel(model, await response.text());
        }),
      );
      if (loadedModels.some((model) => !model)) return;
      beforeModel = loadedModels[0];
      afterModel = loadedModels[1];
      comparisonCandidates = loadedModels.slice(1);
      timelineModels = loadedModels;
      selectedTimelineModelIds = loadedModels.map(getModelKey);
      lastComparisonBaselineKey = `${beforeModel.id}:${getBaseModelName(beforeModel.name)}`;
      changesOpen = true;
    } catch (err) {
      console.error("Failed to load Change Analysis demo models:", err);
    }
  }

  async function updateChangeSummary(beforeXml, afterXml, key) {
    const requestId = ++diffRequestId;
    lastDiffKey = key;
    diffLoading = true;
    diffError = "";
    hoveredChangeId = "";
    selectedChangeId = "";

    try {
      const changes = await diffBpmnModels(beforeXml, afterXml);
      if (requestId !== diffRequestId) return;
      changeSummary = changes.rows;
      beforeMarkers = changes.beforeMarkers;
      afterMarkers = changes.afterMarkers;
    } catch (err) {
      if (requestId !== diffRequestId) return;
      console.error("Failed to diff BPMN models:", err);
      changeSummary = [];
      beforeMarkers = [];
      afterMarkers = [];
      diffError = "Could not calculate BPMN changes.";
    } finally {
      if (requestId === diffRequestId) diffLoading = false;
    }
  }

  function toggleChangeFilter(change) {
    changeFilterMode = "types";
    selectedChangeFilters = selectedChangeFilters.includes(change)
      ? selectedChangeFilters.filter((filter) => filter !== change)
      : [...selectedChangeFilters, change];
    if (selectedChangeFilters.length === 0) changeFilterMode = "none";
    selectedChangeId = "";
  }

  function showAllChanges() {
    changeFilterMode = "all";
    selectedChangeFilters = [];
    selectedChangeId = "";
  }

  function hideChangeMarkers() {
    changeFilterMode = "none";
    selectedChangeFilters = [];
    selectedChangeId = "";
  }

  function toggleFullscreenPane(pane) {
    fullscreenPane = fullscreenPane === pane ? "" : pane;
  }

  function toggleSelectedChange(id) {
    selectedChangeId = selectedChangeId === id ? "" : id;
  }

  function getPaxbotContext() {
    return {
      projectId: idproject,
      view: "Change Analysis",
      beforeModel: beforeModel
        ? { name: beforeModel.name, date: beforeModel.date }
        : null,
      afterModel: afterModel
        ? { name: afterModel.name, date: afterModel.date }
        : null,
      changeFilterMode,
      selectedChangeFilters,
      selectedChangeId,
      changeCounts,
      changes: changeSummary.slice(0, 30),
      instructionContext:
        "Use this context to explain BPMN model differences, added/removed/changed/layout-changed elements, and what the user should inspect in the Change Analysis view.",
    };
  }

  async function requestPaxbotAnswer(question, history, responseLanguage) {
    const response = await fetchWithSession("/api/damprojects/paxbot/chat", {
      signal: abortSignal,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        question,
        history,
        context: {
          ...getPaxbotContext(),
          responseLanguage,
        },
      }),
    });
    const responseText = await response.text();
    let result = {};
    try {
      result = responseText ? JSON.parse(responseText) : {};
    } catch (parseError) {
      throw new Error(
        `Paxbot endpoint returned ${response.status || "an invalid response"}`,
      );
    }
    if (!response.ok || result.status === "error") {
      throw new Error(result.message || "Paxbot request failed");
    }
    return result.answer;
  }
  onMount(loadSavedProcessModelAvailability);
</script>

<Header
  paddingRight={false}
  section={isProcessIntelligenceChangeAnalysis
    ? "Digital Twin"
    : "Change Analysis"}
  projectNameOverride={isProcessIntelligenceChangeAnalysis
    ? "Digital Twin"
    : ""}
  projectHref={isProcessIntelligenceChangeAnalysis ? ROUTES.dam.root : ""}
  area={isProcessIntelligenceChangeAnalysis
    ? "Process Intelligence"
    : "Workflow Analysis"}
  areaClickable={!isProcessIntelligenceChangeAnalysis}
  breadcrumb="Change Analysis"
  tooltipText="Compare process snapshots and analyze changes between process states."
>
  <Filter allFiltersDisabled hideProcessFlow />
</Header>

{#if modelPickerOpen}
  <ProcessModelSelectionModal
    bind:open={modelPickerOpen}
    sideLabel="Baseline"
    on:select={selectStoredModel}
  />
{/if}

<SectionWrapper
  fullscreen={fullscreenPane !== ""}
  fitScreen
  emptyState={changeAnalysisEmptyState}
>
  <div slot="main" class="main" data-project-id={idproject}>
    <PaxbotChat
      bind:open={paxbotOpen}
      heading="Pax AI"
      subheading="Change Analysis Assistant"
      requestAnswer={requestPaxbotAnswer}
    />
    <div class="canvas_surface">
      <div class="graph_left_toolbar">
        <OverflowMenu style="width: auto;">
          <div slot="menu">
            <div
              class="analysis_view_trigger"
              title={activeAnalysisViewOption.text}
            >
              <img
                src={`/img/assistant/icons/${activeAnalysisViewOption.icon}.svg`}
                alt=""
              />
            </div>
          </div>
          {#each analysisViewOrder as view}
            <OverflowMenuItem
              class={analysisView === view ? "active_color_option" : ""}
              text={analysisViewOptions[view].text}
              on:click={() => setAnalysisView(view)}
              primaryFocus={analysisView === view}
            />
          {/each}
        </OverflowMenu>
      </div>
      <div class="graph_top_right_toolbar">
        {#if analysisView !== "comparison" && (displayedModel || analysisView === "timeline")}
          <Button
            kind="secondary"
            icon={DOWNLOAD_ICON}
            iconDescription={analysisView === "timeline"
              ? "Download change history"
              : "Download colored model as SVG"}
            on:click={downloadCurrentView}
          />
        {/if}
        <div class="paxbot_button" class:active={paxbotOpen}>
          <Button
            kind="secondary"
            icon={BOT_ICON}
            iconDescription="Paxbot"
            on:click={() => (paxbotOpen = !paxbotOpen)}
          />
        </div>
      </div>

      <section class="pane" aria-label="Process model change preview">
        {#if analysisView === "timeline"}
          <div class="timeline_view">
            {#if timelineLoading}
              <span class="timeline_state">Loading model history…</span>
            {:else if timelineError}
              <span class="timeline_state">{timelineError}</span>
            {:else if timelineDataSets.some((dataSet) => dataSet.data.length)}
              <LineChartNeu
                bind:this={lineChart}
                height="100%"
                width="100%"
                variant="clean"
                axes={["y", "y", "y"]}
                dataSets={timelineDataSets}
              />
            {:else}
              <span class="timeline_state">No model history selected.</span>
            {/if}
          </div>
          <div class="timeline_fullscreen_button">
            <Button
              kind="secondary"
              icon={fullscreenPane === TIMELINE_ICON
                ? FULLSCREEN_OFF_ICON
                : FULLSCREEN_ICON}
              iconDescription={fullscreenPane === "timeline"
                ? "Exit fullscreen"
                : "Fullscreen"}
              on:click={() => toggleFullscreenPane("timeline")}
            />
          </div>
        {:else if analysisView === "comparison" && beforeModel}
          <div class="model_comparison_view">
            <div class="model_comparison_pane">
              <BpmnPreview
                bpmnXml={beforeModel.bpmnXml}
                markers={filteredBeforeMarkers}
                hoverElementId={activeChangeId}
                actionsRightOffset="1rem"
                showFullscreen={false}
                fullscreen={fullscreenPane === "before"}
                on:fullscreen={() => toggleFullscreenPane("before")}
              />
            </div>
            <div class="model_comparison_divider" />
            <div class="model_comparison_pane">
              {#if afterModel}
                <BpmnPreview
                  bpmnXml={afterModel.bpmnXml}
                  markers={filteredAfterMarkers}
                  hoverElementId={activeChangeId}
                  actionsRightOffset="0"
                  fullscreen={fullscreenPane === "after"}
                  on:fullscreen={() => toggleFullscreenPane("after")}
                />
              {:else}
                <div class="model_placeholder">
                  <span>Select comparison model</span>
                </div>
              {/if}
            </div>
          </div>
        {:else if displayedModel}
          <BpmnPreview
            bind:this={bpmnPreview}
            bpmnXml={displayedModel.bpmnXml}
            markers={displayedMarkers}
            hoverElementId={activeChangeId}
            actionsRightOffset="0"
            fullscreen={fullscreenPane === "before"}
            on:fullscreen={() => toggleFullscreenPane("before")}
          />
        {:else}
          <div class="model_placeholder">
            <Button
              text="Select Baseline"
              kind="secondary"
              size="field"
              on:click={openModelPicker}
            />
          </div>
        {/if}
      </section>

      {#if analysisView !== "timeline" && hasBothModels && changesOpen}
        <ChangeListTable
          {changeFilters}
          {changeFilterMode}
          {selectedChangeFilters}
          {changeSummary}
          {filteredChangeSummary}
          {diffLoading}
          {diffError}
          {hoveredChangeId}
          {selectedChangeId}
          on:all={showAllChanges}
          on:none={hideChangeMarkers}
          on:toggle={(event) => toggleChangeFilter(event.detail)}
          on:hover={(event) => (hoveredChangeId = event.detail)}
          on:select={(event) => toggleSelectedChange(event.detail)}
          on:close={() => (changesOpen = false)}
        />
      {/if}

      {#if analysisView !== "timeline" && hasBothModels}
        <div class="bottom_left_controls">
          <Button
            text="List of Changes"
            kind="secondary"
            size="field"
            iconRight
            icon={changesOpen ? CHEVRON_DOWN_ICON : CHEVRON_UP_ICON}
            on:click={() => (changesOpen = !changesOpen)}
          />
          {#if analysisView === "model"}
            <ContentSwitcher
              options={["Baseline", "Comparison"]}
              disabled={!afterModel ? [1] : []}
              bind:selected={modelViewSelection}
              fitContent
            />
          {/if}
        </div>
      {/if}
    </div>
  </div>

  <div
    slot="menu"
    class="comparison_sidebar"
    aria-label="Change analysis selection"
  >
    {#if analysisView === "timeline"}
      <Accordion heading="Changes" open={true}>
        {#if beforeModel && latestTimelineModel}
          {#key `${beforeModel?.id || ""}:${latestTimelineModel?.id || ""}`}
            <ModelQuantificationCard
              model={latestTimelineModel}
              compareModel={beforeModel}
              side="summary"
              showHeader={false}
            />
          {/key}
        {:else}
          <span class="comparison_state">No changes selected.</span>
        {/if}
      </Accordion>

      <Accordion heading="Baseline" open={true}>
        {#if beforeModel}
          <div class="selection_card">
            <span class="selection_name">
              <strong>{getBaseModelName(beforeModel.name)}</strong>
            </span>
            <span class="selection_date">
              {beforeModel.date || "No date available"}
            </span>
          </div>
          <div class="sidebar_picker_button">
            <Button
              text="Change Baseline"
              kind="secondary"
              size="field"
              on:click={openModelPicker}
            />
          </div>
        {:else}
          <div class="sidebar_picker_button">
            <Button
              text="Select Baseline"
              kind="secondary"
              size="field"
              on:click={openModelPicker}
            />
          </div>
        {/if}
      </Accordion>

      {#if beforeModel}
        <Accordion heading="Model History" open={true}>
          <div class="comparison_section timeline_selection">
            <p>Select the saved models shown in the chart.</p>
            <div class="comparison_search">
              <img src="/img/assistant/icons/search.svg" alt="" />
              <input
                type="search"
                bind:value={timelineSearch}
                placeholder="Search existing versions"
                aria-label="Search model history"
              />
            </div>
            <div class="comparison_list">
              {#if timelineLoading}
                <span class="comparison_state">Loading models…</span>
              {:else if timelineError}
                <span class="comparison_state">{timelineError}</span>
              {:else if !visibleTimelineModels.length}
                <span class="comparison_state">No matching versions found.</span
                >
              {:else}
                {#each visibleTimelineModels as model (getModelKey(model))}
                  <label class="comparison_card timeline_model_card">
                    <input
                      class="timeline_checkbox"
                      type="checkbox"
                      checked={selectedTimelineModelIds.includes(
                        getModelKey(model),
                      )}
                      on:change={() => toggleTimelineModel(model)}
                    />
                    <span class="comparison_name">
                      <strong>{model.name?.replace(/\.bpmn$/i, "")}</strong>
                      <small>{model.area || getBaseModelName(model.name)}</small
                      >
                    </span>
                    <span>
                      <strong
                        >{formatDateValue(model.created_at) ||
                          model.date ||
                          "–"}</strong
                      >
                      <small>Date</small>
                    </span>
                    <span>
                      <strong
                        >{formatDuration(
                          model.avgTimeSeconds ?? model.avg_time_seconds,
                        )}</strong
                      >
                      <small>Avg Time</small>
                    </span>
                  </label>
                {/each}
              {/if}
            </div>
          </div>
        </Accordion>
      {/if}
    {:else}
      <Accordion heading="Changes" open={true}>
        {#if hasBothModels}
          {#key `${beforeModel?.id || ""}:${afterModel?.id || ""}`}
            <ModelQuantificationCard
              model={afterModel}
              compareModel={beforeModel}
              side="summary"
              showHeader={false}
            />
          {/key}
        {:else}
          <span class="comparison_state">No comparison selected.</span>
        {/if}
      </Accordion>

      <Accordion heading="Baseline" open={true}>
        {#if beforeModel}
          <div class="selection_card">
            <span class="selection_name">
              <strong>{getBaseModelName(beforeModel.name)}</strong>
            </span>
            <span class="selection_date">
              {beforeModel.date || "No date available"}
            </span>
          </div>
          <div class="sidebar_picker_button">
            <Button
              text="Change Baseline"
              kind="secondary"
              size="field"
              on:click={openModelPicker}
            />
          </div>
        {:else}
          <div class="sidebar_picker_button">
            <Button
              text="Select Baseline"
              kind="secondary"
              size="field"
              on:click={openModelPicker}
            />
          </div>
        {/if}
      </Accordion>

      {#if beforeModel}
        <div
          role="presentation"
          on:click={() => (comparisonPanelOpen = true)}
          on:keydown={() => {}}
        >
          <Accordion open={comparisonPanelOpen}>
            <span slot="heading" class="accordion-heading">
              Comparison
              <Tooltip
                text="Shows saved versions of the selected baseline model."
              />
            </span>
            <div class="comparison_section">
              <div class="comparison_list">
                {#if comparisonLoading}
                  <span class="comparison_state">Loading versions…</span>
                {:else if comparisonError}
                  <span class="comparison_state">{comparisonError}</span>
                {:else if !visibleComparisonCandidates.length}
                  <span class="comparison_state">No versions found.</span>
                {:else}
                  {#each visibleComparisonCandidates as candidate (candidate.id)}
                    <button
                      type="button"
                      class="comparison_card"
                      class:selected={afterModel?.id == candidate.id}
                      on:click={() => selectComparisonModel(candidate)}
                    >
                      <span class="comparison_name">
                        <strong>{getBaseModelName(candidate.name)}</strong>
                      </span>
                      <span class="selection_date">
                        {formatDateValue(candidate.created_at) ||
                          candidate.date ||
                          "No date available"}
                      </span>
                    </button>
                  {/each}
                {/if}
              </div>
            </div>
          </Accordion>
        </div>
      {/if}
    {/if}
  </div>

  <div slot="emptyState" class="empty_state_text">
    <div class="empty_state_title">No saved process models available.</div>
    <div class="empty_state_hint">
      Save a process model in Process Explorer first to use Change Analysis.
    </div>
  </div>
</SectionWrapper>

<style>
  .empty_state_title,
  .empty_state_hint {
    font-size: 1rem;
  }

  .empty_state_title {
    color: var(--pax-black);
  }

  .empty_state_hint {
    color: var(--pax-grey);
    margin-top: 0.5rem;
  }

  .main {
    height: 100%;
    position: relative;
    width: 100%;
  }

  .canvas_surface {
    height: 100%;
    isolation: isolate;
    overflow: hidden;
    position: relative;
    width: 100%;
  }

  .bottom_left_controls {
    align-items: center;
    bottom: 0;
    display: flex;
    gap: 0.5rem;
    left: 0;
    position: absolute;
    pointer-events: auto;
    z-index: 40;
  }

  .bottom_left_controls :global(.grid_container) {
    height: 2.5rem;
    padding: 0 0.25rem;
  }

  .bottom_left_controls :global(.grid_item) {
    height: 2rem;
  }

  .graph_left_toolbar,
  .graph_top_right_toolbar {
    align-items: center;
    display: flex;
    gap: 0.5rem;
    position: absolute;
    top: 0.25rem;
    pointer-events: auto;
    z-index: 40;
  }

  .graph_left_toolbar {
    left: 0.25rem;
  }

  .graph_left_toolbar :global(.bx--overflow-menu) {
    background-color: var(--pax-black);
    border-radius: 0.5rem;
    height: 3rem;
    width: 3rem;
  }

  .graph_left_toolbar :global(.bx--overflow-menu:hover) {
    background-color: #2a2a2f;
  }

  .analysis_view_trigger {
    align-items: center;
    display: flex;
    height: 3rem;
    justify-content: center;
    width: 3rem;
  }

  :global(.active_color_option .bx--overflow-menu-options__btn) {
    background-color: #ffe8dd;
    border-left: 0.25rem solid var(--pax-orange);
    font-weight: 600;
  }

  .graph_top_right_toolbar {
    right: 0.25rem;
  }

  .graph_left_toolbar :global(.button_wrapper),
  .graph_top_right_toolbar :global(.button_wrapper) {
    height: 3rem;
    width: 3rem;
  }

  .graph_left_toolbar :global(img),
  .graph_top_right_toolbar :global(img) {
    filter: brightness(0) invert(1);
    height: 1.25rem;
    object-fit: contain;
    width: 1.25rem;
  }

  .analysis_view_trigger :global(img) {
    filter: none;
  }

  .paxbot_button.active :global(.button_wrapper) {
    background: var(--pax-orange);
  }

  .pane {
    align-items: center;
    display: flex;
    inset: 0;
    justify-content: center;
    min-width: 0;
    position: absolute;
    width: 100%;
    z-index: 0;
  }

  .model_comparison_view {
    box-sizing: border-box;
    display: grid;
    grid-template-columns: minmax(0, 1fr) 2px minmax(0, 1fr);
    height: 100%;
    width: 100%;
  }

  .model_comparison_pane {
    background: var(--pax-white);
    box-sizing: border-box;
    height: 100%;
    min-width: 0;
    overflow: hidden;
    position: relative;
    width: 100%;
  }

  .model_comparison_divider {
    background: var(--pax-black);
    height: 100%;
    width: 2px;
  }

  .timeline_view {
    box-sizing: border-box;
    height: 100%;
    padding: 4.25rem 0 0;
    width: 100%;
  }

  .timeline_fullscreen_button {
    bottom: 0.25rem;
    position: absolute;
    right: 0.25rem;
    z-index: 40;
  }

  .timeline_fullscreen_button :global(.button_wrapper) {
    height: 3rem;
    width: 3rem;
  }

  .timeline_fullscreen_button :global(img) {
    filter: brightness(0) invert(1);
    height: 1.25rem;
    object-fit: contain;
    width: 1.25rem;
  }

  .timeline_state {
    align-items: center;
    color: #6f7780;
    display: flex;
    height: 100%;
    justify-content: center;
  }

  .canvas_surface :global(.djs-container) {
    z-index: 0 !important;
  }

  .canvas_surface :global(.preview_actions),
  .canvas_surface :global(.change_card) {
    pointer-events: auto;
    z-index: 40;
  }

  .comparison_sidebar {
    background: transparent;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    height: 100%;
    min-height: 0;
    overflow-x: hidden;
    overflow-y: auto;
    scrollbar-width: none;
  }

  .main :global(.paxbot_chat_window) {
    bottom: 5.85rem !important;
    position: absolute !important;
    right: 1rem !important;
    z-index: 50 !important;
  }

  .main :global(.paxbot_chat_mascot) {
    bottom: -0.65rem !important;
    position: absolute !important;
    right: 1.55rem !important;
    z-index: 51 !important;
  }

  .comparison_sidebar::-webkit-scrollbar {
    display: none;
  }

  .accordion-heading {
    align-items: center;
    display: inline-flex;
    gap: 0.4rem;
  }

  .timeline_checkbox {
    cursor: pointer;
    height: 1rem;
    margin: 0;
    width: 1rem;
  }

  .comparison_sidebar :global(.quantification_card) {
    border: 0;
    border-radius: 0;
    box-sizing: border-box;
    max-width: none;
    min-width: 100%;
    width: 100% !important;
  }

  .comparison_section p {
    color: #6f7780;
    font-size: 0.75rem;
    margin: -0.35rem 0 0;
  }

  .selection_card,
  .comparison_card {
    background: var(--pax-white);
    border: 1px solid var(--border-color);
    border-radius: 0.5rem;
    color: var(--pax-black);
    text-align: left;
    width: 100%;
  }

  .selection_card {
    align-items: center;
    display: flex;
    gap: 1rem;
    justify-content: space-between;
    min-height: 3.8rem;
    padding: 0.65rem 0.75rem;
  }

  .selection_card + .sidebar_picker_button {
    margin-top: 0.5rem;
  }

  .selection_name,
  .comparison_card .comparison_name {
    min-width: 0;
  }

  .selection_card strong,
  .comparison_card strong,
  .comparison_card small {
    display: block;
  }

  .selection_date,
  .comparison_card small,
  .comparison_state {
    color: #6f7780;
    font-size: 0.75rem;
  }

  .selection_date {
    flex: 0 0 auto;
    text-align: right;
  }

  .comparison_search {
    align-items: center;
    border: 1px solid var(--border-color);
    border-radius: 0.5rem;
    box-sizing: border-box;
    display: flex;
    height: 2.75rem;
    padding: 0 0.75rem;
    width: 100%;
  }

  .comparison_search img {
    height: 1rem;
    margin-right: 0.6rem;
    opacity: 0.65;
    width: 1rem;
  }

  .comparison_search input {
    border: 0;
    box-sizing: border-box;
    color: var(--pax-black);
    flex: 1 1 auto;
    font-size: 0.8rem;
    height: 100%;
    min-width: 0;
    outline: none;
    padding: 0;
    width: 100%;
  }

  .comparison_list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .comparison_card {
    align-items: center;
    cursor: pointer;
    display: flex;
    gap: 1rem;
    justify-content: space-between;
    min-height: 3.8rem;
    padding: 0.65rem 0.75rem;
  }

  .timeline_model_card {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto auto;
  }

  .comparison_card:hover,
  .comparison_card.selected {
    border-color: var(--pax-orange);
  }

  .selection_name strong,
  .selection_date,
  .comparison_card .comparison_name strong,
  .comparison_card .comparison_name small {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .comparison_state {
    padding: 0.75rem 0;
  }

  .model_placeholder {
    align-items: center;
    color: var(--pax-grey);
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    height: 18rem;
    justify-content: center;
    max-width: 32rem;
    text-align: center;
    width: 100%;
  }

  .sidebar_picker_button {
    width: 100%;
  }

  @media (max-width: 1150px) {
    .timeline_model_card {
      align-items: flex-start;
      grid-template-columns: auto minmax(0, 1fr);
    }

    .timeline_model_card .comparison_name {
      grid-column: auto;
    }
  }
</style>
